import { gql } from "@apollo/client";
import moment from "moment";
import { captureException } from "@sentry/browser";

import { getDeliveryHourFromSlot } from "../helpers/delivery";
import type { AppState } from "../reducers/index";

import { apollo, AppDispatch, AppThunk, getWallet } from "./index";
import {
  COLLECT_CART_LOADING,
  COLLECT_CART_ADD,
  COLLECT_CONFIG_ERROR,
  COLLECT_CONFIG_LOADING,
  COLLECT_CONFIG_SUCCESS,
  COLLECT_INVENTORY_LOADING,
  COLLECT_INVENTORY_SUCCESS,
  COLLECT_INVENTORY_ERROR,
  COLLECT_INVENTORY_FILTER,
  COLLECT_CART_ERROR,
  COLLECT_CART_SUCCESS,
  COLLECT_CART_DELETE,
  COLLECT_ORDER_ERROR,
  COLLECT_ORDER_LOADING,
  COLLECT_ORDER_SUCCESS,
  COLLECT_DELIVERY_SUCCESS,
  COLLECT_DELIVERY_LOADING,
  COLLECT_DELIVERY_ERROR,
  COLLECT_CANCELORDERMODAL_OPEN,
  COLLECT_CANCELORDERMODAL_CLOSE,
  COLLECT_CANTEENLOCATION_SET,
  APP_LOADING_START,
  APP_LOADING_END,
  COLLECT_RESET_STATE,
} from "./types";
import { checkIfConditionsAreFulfilled } from "../helpers/cartRequirements";
import * as rootNavigation from "../navigators/RootNavigatorRef";
import { addToast } from "./toast";
import type { TranslateOptions } from "@/i18n";
import { translateMessage } from "@/helpers/translation";

const translateCollectMessage = (key: string, options?: TranslateOptions) =>
  translateMessage(`collect.${key}`, options);

const getCollectMessages = () => ({
  oopsTitle: translateCollectMessage("titles.oops", { defaultValue: "Oups !" }),
  successTitle: translateCollectMessage("titles.success", { defaultValue: "Succès" }),
  orderPlacedTitle: translateCollectMessage("titles.orderPlaced", {
    defaultValue: "Commande passée",
  }),
  orderCanceledTitle: translateCollectMessage("titles.orderCanceled", {
    defaultValue: "Commande annulée",
  }),
  genericError: translateCollectMessage("errors.generic", {
    defaultValue: "Une erreur est survenue, veuillez réessayer.",
  }),
  orderConnectionError: translateCollectMessage("errors.orderConnection", {
    defaultValue:
      "La commande n'a pas pu être passée, veuillez vérifier votre connexion puis réessayez.",
  }),
  cartComputeError: translateCollectMessage("errors.cartCompute", {
    defaultValue:
      "Une erreur est survenue lors du calcul du panier, le produit n'est peut-être plus disponible.",
  }),
  missingPickup: translateCollectMessage("errors.missingPickup", {
    defaultValue: "Veuillez sélectionner un point de retrait puis réessayez.",
  }),
  notEnoughCredits: translateCollectMessage("errors.notEnoughCredits", {
    defaultValue: "Vous n'avez pas assez de crédits pour passer cette commande.",
  }),
  emptyCart: translateCollectMessage("errors.emptyCart", {
    defaultValue: "Votre panier est vide, veuillez ajouter des produits.",
  }),
  missingAmount: (amount: string) =>
    translateCollectMessage("errors.missingAmount", {
      defaultValue: "Encore {{amount}} € pour commander !",
      params: { amount },
    }),
  missingCategories: (categories: string) =>
    translateCollectMessage("errors.missingCategories", {
      defaultValue: "Prenez {{categories}} pour passer à la suite !",
      params: { categories },
    }),
  orderPlaced: translateCollectMessage("success.orderPlaced", {
    defaultValue: "Votre commande a bien été passée.",
  }),
  orderCanceled: translateCollectMessage("success.orderCanceled", {
    defaultValue: "Votre commande a bien été annulée.",
  }),
});

export const getClickAndCollectConfiguration =
  (withInventoryLoading: boolean = true): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getCollectMessages();
    dispatch({ type: APP_LOADING_START });
    const { collect } = getState();

    if (collect.configLoading) {
      // abort concurrent requests
      return;
    }

    await apollo().cache.reset();

    try {
      const req: any = await apollo().query({
        query: gql`
          {
            clickAndCollectConfiguration {
              hasClickAndCollectEnabled
              isClickAndCollectOrderPossible
              clickAndCollectOrderPossibleMessage
              nextClickAndCollectDeliverySlot {
                slotStartAt
                slotEndAt
                limitOrderingDate
              }
              clickAndCollectCanteenLocations {
                id
                isClickCollectDeliveryPoint
                name
                address
              }
              clickAndCollectCartRequirements {
                minimumPrice
                minimumCategories
              }
            }
          }
        `,
      });

      await dispatch({
        type: COLLECT_CONFIG_SUCCESS,
        configuration: req.data.clickAndCollectConfiguration,
      });

      // If only one canteenLocation select it
      if (req.data.clickAndCollectConfiguration?.clickAndCollectCanteenLocations?.length === 1) {
        await dispatch({
          type: COLLECT_CANTEENLOCATION_SET,
          canteenLocationId:
            req.data.clickAndCollectConfiguration.clickAndCollectCanteenLocations[0].id,
        });
      }

      if (withInventoryLoading) {
        await dispatch(getClickAndCollectInventory());
      }
    } catch (err) {
      dispatch(
        addToast({
          type: "error",
          message: messages.genericError,
        }),
      );
      captureException(err);
      dispatch({
        type: COLLECT_CONFIG_ERROR,
      });
      dispatch({ type: APP_LOADING_END });
    } finally {
      dispatch({ type: APP_LOADING_END });
    }
  };

export const getClickAndCollectInventory =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getCollectMessages();
    let { collect } = getState();

    if (collect.inventoryLoading) {
      // abort concurrent requests
      return;
    }

    dispatch({ type: APP_LOADING_START });

    if (!collect?.configuration?.nextClickAndCollectDeliverySlot?.slotStartAt) {
      await dispatch(getClickAndCollectConfiguration(false));

      collect = getState().collect;
    }

    if (collect?.configuration.isClickAndCollectOrderPossible === false) {
      return dispatch({
        type: COLLECT_INVENTORY_SUCCESS,
        inventory: [],
      });
    }

    const inventoryDate = moment(
      collect?.configuration?.nextClickAndCollectDeliverySlot?.slotStartAt,
    ).format();

    await apollo().cache.reset();

    try {
      const req: any = await apollo().query({
        variables: { date: inventoryDate },
        query: gql`
          query ($date: DateTime!) {
            clickAndCollectInventory(date: $date) {
              products {
                id
                localizedName
                available
                price
                localizedDescription
                unavailabilityPublicMessage
                vatRate
                pictureUrl
                servingSizeWeight
                isHot
                category
                localizedHeatingInstructions
                servingSizeWeight
                description
                protein
                fat
                saturatedFat
                carbohydrate
                sugar
                dietaryFiber
                salt
                kilocalories
                kilojoules
                allergens {
                  localizedName
                }
                allergenTraces {
                  localizedName
                }
                brand {
                  id
                  name
                  key
                }
                localizedIngredientsList
                ingredients {
                  percentage
                  ingredient {
                    localizedName
                    ingredients {
                      percentage
                      ingredient {
                        localizedName
                      }
                    }
                  }
                }
                canteenTags {
                  id
                  localizedName
                }
              }
            }
          }
        `,
      });

      await dispatch({
        type: COLLECT_INVENTORY_SUCCESS,
        inventory: req.data.clickAndCollectInventory,
      });

      const categoryToFilter = collect.category || "Dish";

      await dispatch(filterCollectInventory(categoryToFilter));
    } catch (err) {
      captureException(err);
      dispatch(
        addToast({
          type: "error",
          message: messages.genericError,
        }),
      );
      dispatch({ type: COLLECT_INVENTORY_ERROR });
    } finally {
      dispatch({ type: APP_LOADING_END });
    }
  };

export const setClickAndCollectCanteenLocation =
  (canteenLocationId: number | string): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: COLLECT_CANTEENLOCATION_SET, canteenLocationId });
  };

export const addItemToClickAndCollectCart =
  (itemId: number | string, quantity: number = 1): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: COLLECT_CART_ADD, item: { id: itemId, quantity } });
    dispatch(computeClickAndCollectCart());
  };

export const deleteItemFromClickAndCollectCart =
  (itemId: number | string): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: COLLECT_CART_DELETE, itemId });
    dispatch(computeClickAndCollectCart());
  };

export const computeClickAndCollectCart =
  (isARetry = false): AppThunk =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getCollectMessages();
    dispatch({ type: COLLECT_CART_LOADING });

    let collect = getState().collect;

    if (!collect?.configuration?.nextClickAndCollectDeliverySlot?.slotStartAt) {
      await dispatch(getClickAndCollectConfiguration());

      collect = getState().collect;
    }

    const deliveryDay = moment(
      collect?.configuration?.nextClickAndCollectDeliverySlot?.slotStartAt,
    ).format();

    try {
      if (!deliveryDay) {
        throw new Error("Invalid delivery date");
      }

      const req: any = await apollo().query({
        variables: { deliveryDay, cartProducts: collect.cartItems },
        query: gql`
          query ($deliveryDay: DateTime!, $cartProducts: [ClickAndCollectCartProductInput!]!) {
            previewClickAndCollectOrder(deliveryDay: $deliveryDay, cartProducts: $cartProducts) {
              totalFormulaDiscounts
              totalGrants
              totalFees
              totalWithTaxes
              totalWithoutTaxes
              rawTotalWithTaxes
              hasEnoughCredits
            }
          }
        `,
      });
      dispatch({
        type: COLLECT_CART_SUCCESS,
        cart: req.data.previewClickAndCollectOrder,
      });
    } catch (err) {
      captureException(err);
      if (isARetry) {
        // if cart computing failed twice, show an error
        dispatch({ type: COLLECT_CART_ERROR });
        dispatch({
          type: COLLECT_CART_DELETE,
          itemId: collect.lastCartItem?.id || null,
        });
        dispatch(
          addToast({
            type: "error",
            message: messages.cartComputeError,
          }),
        );
      } else {
        // if an error occured while computing cart, try one more time (can be a network error)
        dispatch(computeClickAndCollectCart(true));
      }
    }
  };

export const createClickAndCollectOrder =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getCollectMessages();
    let collect = getState().collect;
    // Guard against double submit while the request is in progress.
    if (collect?.orderLoading) {
      return;
    }

    if (!collect?.configuration?.nextClickAndCollectDeliverySlot?.slotStartAt) {
      await dispatch(getClickAndCollectConfiguration());

      collect = getState().collect;
    }

    const deliveryHour = moment(collect.configuration.nextClickAndCollectDeliverySlot.slotStartAt)
      .add(
        getDeliveryHourFromSlot(
          collect.configuration.nextClickAndCollectDeliverySlot.slotStartAt,
          collect.configuration.nextClickAndCollectDeliverySlot.slotEndAt,
        ),
        "minutes",
      )
      .format();

    if (!collect.canteenLocationId) {
      return dispatch(
        addToast({
          type: "error",
          title: messages.oopsTitle,
          message: messages.missingPickup,
        }),
      );
    }

    if (!collect.hasEnoughCredits) {
      return dispatch(
        addToast({
          type: "error",
          title: messages.oopsTitle,
          message: messages.notEnoughCredits,
        }),
      );
    }

    if ((collect.cartItems?.length || 0) === 0) {
      return dispatch(
        addToast({
          type: "error",
          title: messages.oopsTitle,
          message: messages.emptyCart,
        }),
      );
    }

    const doesCartItemsMetConditionsToBeOrdered = Boolean(
      collect.configuration &&
        collect.inventory &&
        checkIfConditionsAreFulfilled(
          collect.configuration.clickAndCollectCartRequirements,
          collect.cartItems,
          collect.inventory.products,
        ),
    );
    if (!doesCartItemsMetConditionsToBeOrdered) {
      if (collect.configuration.clickAndCollectCartRequirements.length === 1) {
        const [missingAmount, missingCategories] = collect.configuration
          .clickAndCollectCartRequirements.length
          ? collect.configuration.clickAndCollectCartRequirements[0]
          : [0, []];

        const explicitMissingAmount =
          missingAmount > 0 ? messages.missingAmount(String(missingAmount)) : "";

        const explicitMissingCategories = missingCategories.length
          ? messages.missingCategories(missingCategories.join(", "))
          : "";

        return dispatch(
          addToast({
            type: "error",
            title: messages.oopsTitle,
            message: `${explicitMissingAmount} ${explicitMissingCategories}`.trim(),
          }),
        );
      }
    }

    // Disable submit and show loading state during mutation.
    dispatch({ type: COLLECT_ORDER_LOADING });

    try {
      const req = await apollo().mutate<any>({
        variables: {
          deliveryDay: deliveryHour,
          cartProducts: collect.cartItems,
          expectedTotalPrice: collect.cartTotalWithTaxes,
          canteenLocationId: collect.canteenLocationId,
        },
        mutation: gql`
          mutation (
            $deliveryDay: DateTime!
            $cartProducts: [ClickAndCollectCartProductInput!]!
            $expectedTotalPrice: Int!
            $canteenLocationId: String!
          ) {
            createClickAndCollectOrder(
              deliveryDay: $deliveryDay
              cartProducts: $cartProducts
              expectedTotalPrice: $expectedTotalPrice
              canteenLocationId: $canteenLocationId
            )
          }
        `,
      });

      if (req.data.createClickAndCollectOrder === "OK") {
        dispatch({ type: COLLECT_ORDER_SUCCESS });
        dispatch(
          addToast({
            type: "success",
            title: messages.orderPlacedTitle,
            message: messages.orderPlaced,
          }),
        );
        dispatch(getClickAndCollectConfiguration());
        dispatch(getWallet());
      } else {
        dispatch(
          addToast({
            type: "error",
            message: messages.genericError,
          }),
        );
        throw new Error("Order create failed");
      }
    } catch (err) {
      captureException(err);
      dispatch(
        addToast({
          type: "error",
          message: messages.orderConnectionError,
        }),
      );
      dispatch({
        type: COLLECT_ORDER_ERROR,
        message: messages.orderConnectionError,
      });
    }
  };

export const getClickAndCollectDeliveryTracking =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getCollectMessages();
    try {
      const req: any = await apollo().query({
        query: gql`
          query {
            clickAndCollectDeliveryTracking {
              id
              status
              canCustomerStillCancelThisOrder
              deliveryAddress
              slotStartAt
              slotEndAt
            }
          }
        `,
      });

      const { status } = req.data.clickAndCollectDeliveryTracking;

      if (status === "delivered" || status === "canceled") {
        dispatch({
          type: COLLECT_DELIVERY_SUCCESS,
          deliveryTracking: null,
        });
        //RootNavigation.navigate("home");
      } else {
        dispatch({
          type: COLLECT_DELIVERY_SUCCESS,
          deliveryTracking: req.data.clickAndCollectDeliveryTracking,
        });
      }
    } catch (err) {
      captureException(err);
      dispatch({ type: COLLECT_DELIVERY_ERROR });
      dispatch(
        addToast({
          type: "error",
          message: messages.genericError,
        }),
      );
    }
    dispatch({ type: APP_LOADING_END });
  };

export const openClickAndCollectCancelOrderModal =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch({ type: COLLECT_CANCELORDERMODAL_OPEN });
  };

export const closeClickAndCollectCancelOrderModal =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch({ type: COLLECT_CANCELORDERMODAL_CLOSE });
  };

export const cancelClickAndCollectOrder =
  (orderId: string): any =>
  async (dispatch: AppDispatch) => {
    const messages = getCollectMessages();
    dispatch({ type: APP_LOADING_START });

    try {
      const req = await apollo().mutate<any>({
        variables: { orderId },
        mutation: gql`
          mutation ($orderId: String!) {
            cancelClickAndCollectOrder(orderId: $orderId)
          }
        `,
      });

      if (req.data.cancelClickAndCollectOrder === true) {
        dispatch({ type: APP_LOADING_END });
        dispatch(
          addToast({
            type: "success",
            title: messages.orderCanceledTitle,
            message: messages.orderCanceled,
          }),
        );
        dispatch({
          type: COLLECT_DELIVERY_SUCCESS,
          deliveryTracking: null,
        });
        dispatch(getClickAndCollectConfiguration());
        rootNavigation.resetAndGoTo("home");
      } else {
        throw new Error("Order cancellation failed");
      }
    } catch (err) {
      dispatch(getClickAndCollectConfiguration());
      rootNavigation.resetAndGoTo("home");
      captureException(err);
      dispatch({ type: APP_LOADING_END });
    }
  };

export const filterCollectInventory =
  (category: string | null = null): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const {
      collect: { inventory },
    } = getState();

    if (category) {
      const filteredInventory = JSON.parse(JSON.stringify(inventory));

      filteredInventory.products = inventory.products.filter(
        (product: { category: string }) => product.category === category,
      );

      dispatch({ type: COLLECT_INVENTORY_FILTER, category, filteredInventory });
    }
  };
